import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import 'components/RegShield/regshield-widget-styles.css';

import {
  RegshieldCallbackFn,
  CallbackQuery,
  RegShieldCallbackType,
  CompleteRegShieldRegistration,
  OnRegShieldError,
  OnRegShieldRedirectChallenge
} from 'components/RegShield/types';

import { isDevelopment } from 'utils/checkEnvironment';

const REGSHIELD_WIDGET_CONTAINER_ID = 'regshield-widget-container';

interface RegShieldWidgetProps {
  location: string;
  onRegShieldFinish: CompleteRegShieldRegistration;
  onRegShieldError: OnRegShieldError;
  onRegShieldRedirectChallenge: OnRegShieldRedirectChallenge;
}

export const RegShieldWidget = ({
  location,
  onRegShieldFinish,
  onRegShieldError,
  onRegShieldRedirectChallenge
}: RegShieldWidgetProps) => {
  const { t } = useTranslation();
  const [userId, setUserId] = useState('');
  const [finishedQueryParams, setFinishedQueryParams] = useState<CallbackQuery | null>(null);

  const initializeRegshieldWidget = () => {
    const { __finleapConnectWidget__ } = window;
    if (__finleapConnectWidget__) {
      __finleapConnectWidget__.init(
        REGSHIELD_WIDGET_CONTAINER_ID,
        location,
        handleRegShieldWidgetCallback
      );
    }
  };

  const destroyRegShieldWidget = () => {
    const { __finleapConnectWidget__ } = window;
    __finleapConnectWidget__ && __finleapConnectWidget__.destroy();
  };

  useEffect(() => {
    initializeRegshieldWidget();

    return destroyRegShieldWidget;
  }, [location]); // eslint-disable-line

  useEffect(() => {
    if (!finishedQueryParams) return;

    const { success, trace_id, error_code, state, code } = finishedQueryParams;

    if (success === 'true') {
      return onRegShieldFinish({
        userId,
        state,
        code
      });
    }

    destroyRegShieldWidget();
    onRegShieldError(+error_code, trace_id);
  }, [finishedQueryParams]); // eslint-disable-line

  const displayAccountMismatchError = () => {
    const targetNode = document.getElementById(REGSHIELD_WIDGET_CONTAINER_ID);
    if (targetNode) {
      const ErrorWrapper = document.createElement('div');
      ErrorWrapper.innerHTML = t('reg_shield.error_account_mismatch');
      ErrorWrapper.className = 'account-mismatch-error';
      targetNode.appendChild(ErrorWrapper);
    }
  };

  const handleRegShieldWidgetCallback: RegshieldCallbackFn = (error, data) => {
    if (error) {
      return window.location.reload();
    }

    const { type, payload } = data;

    switch (type) {
      case RegShieldCallbackType.SCREEN_NAVIGATION: {
        break;
      }
      case RegShieldCallbackType.USER_ID: {
        if (payload.userID) {
          setUserId(payload.userID);
        }
        break;
      }
      case RegShieldCallbackType.ACCOUNT_TYPE_MISMATCH:
      case RegShieldCallbackType.ACCOUNT_NOT_FOUND: {
        destroyRegShieldWidget();
        displayAccountMismatchError();
        break;
      }
      case RegShieldCallbackType.REDIRECT_CHALLENGE: {
        const { redirectUrl } = payload;
        onRegShieldRedirectChallenge(redirectUrl);
        break;
      }
      case RegShieldCallbackType.CALLBACK: {
        const { query } = payload;
        const queryParams = query?.query ?? null;

        if (queryParams) {
          setFinishedQueryParams(queryParams);
        }
        break;
      }
      case RegShieldCallbackType.INVALID_WIDGET_LINK: {
        console.warn('Invalid RegShieldWidget link provided!');
        break;
      }
      default:
        if (isDevelopment(import.meta.env.MODE)) {
          throw new Error(`Unsupported RegShieldWidget type: ${type}`);
        } else {
          console.warn(`Unsupported RegShieldWidget type: ${type}`);
        }
    }
  };

  return <div id={REGSHIELD_WIDGET_CONTAINER_ID} data-testid={REGSHIELD_WIDGET_CONTAINER_ID} />;
};
